/**
 * Centralized Role-Based Access Control (RBAC) Utilities for MFA-Admin.
 *
 * Implements granular, segment-based permission evaluation avoiding broad substring matching.
 * Format standard: `${parent}-${child}-${child}${action}`
 * Aliases standard: `View ${child}`, `Create ${child}`, `Edit ${child}`, `Delete ${child}`
 */

import { hasScimPermission, SCIM_PERMISSIONS } from '../pages/ScimIntegrations/scimPermissions';
import { hasGatewayPermission, GATEWAY_PERMISSIONS } from '../pages/GatewayMonitoring/gatewayPermissions';

/**
 * Canonical Features & Modules Catalog
 */
export const FEATURES = {
  DASHBOARD: 'Dashboard',
  DEVICES: 'Devices',
  WINDOWS_AGENT_DEVICES: 'Windows Agent Devices',
  // Retired: AD sync now runs through the gateway page
  // AD_INTEGRATION: 'AD Integration',
  APPLICATIONS: 'Applications',
  ROLE_MASTER: 'Role Master',
  USER_MANAGEMENT: 'User Management',
  PERMISSION_MASTER: 'Permission Master',
  CONNECTIONS: 'Connections', // SCIM
  GATEWAYS: 'Gateways',
};

/**
 * Route-to-Feature Mapping
 */
export const ROUTE_FEATURE_MAP = {
  '/home': FEATURES.DASHBOARD,
  '/dashboard': FEATURES.DASHBOARD,
  '/devices': FEATURES.DEVICES,
  '/windows-agent-devices': FEATURES.WINDOWS_AGENT_DEVICES,
  // Retired: AD sync now runs through the gateway page
  // '/admin/ad-integration': FEATURES.AD_INTEGRATION,
  // '/ad-integration': FEATURES.AD_INTEGRATION,
  '/applications': FEATURES.APPLICATIONS,
  '/role': FEATURES.ROLE_MASTER,
  '/user-management': FEATURES.USER_MANAGEMENT,
  '/permission': FEATURES.ROLE_MASTER,
  '/admin/scim': FEATURES.CONNECTIONS,
  '/admin/gateways': FEATURES.GATEWAYS,
  '/policies': FEATURES.APPLICATIONS,
  '/groups': FEATURES.USER_MANAGEMENT,
  '/logs': FEATURES.DASHBOARD,
};

/**
 * Safely retrieves stored user principal from localStorage or sessionStorage.
 */
export function getStoredUser() {
  try {
    const raw = typeof window !== 'undefined'
      ? (localStorage.getItem('user') || sessionStorage.getItem('user'))
      : null;
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (err) {
    return null;
  }
}

/**
 * Safely retrieves stored token from localStorage or sessionStorage.
 */
export function getStoredToken() {
  try {
    return typeof window !== 'undefined'
      ? (localStorage.getItem('token') || sessionStorage.getItem('token'))
      : null;
  } catch (err) {
    return null;
  }
}

/**
 * Safely retrieves stored login time from localStorage or sessionStorage.
 */
export function getStoredLoginTime() {
  try {
    return typeof window !== 'undefined'
      ? (localStorage.getItem('loginTime') || sessionStorage.getItem('loginTime'))
      : null;
  } catch (err) {
    return null;
  }
}

/**
 * Determines whether the user has full SuperAdmin / Organization Owner privileges.
 * Matches criteria across ScimIntegrations, Dashboard, and backend requirePermission.
 */
export function isOrganizationOwner(user) {
  if (!user || typeof user !== 'object') return false;
  return Boolean(
    user.superadmin === true ||
    user.superadmin === 'true' ||
    user.role === 'SuperAdmin' ||
    user.role === 'Admin' ||
    (user.nameOfOrg && !user.roleId) ||
    user.SuperAdminId
  );
}

/**
 * Internal helper to match action terms against permission string action segments.
 */
const ACTION_TERMS_MAP = {
  read: ['read', 'view'],
  view: ['read', 'view'],
  create: ['create', 'write'],
  write: ['create', 'write'],
  edit: ['edit', 'update'],
  update: ['edit', 'update'],
  delete: ['delete', 'print'],
  print: ['delete', 'print'],
};

/**
 * Checks if a specific permission string belongs to a feature without broad substring collision.
 * Specifically avoids collisions like "Windows Agent Devices" accidentally matching "Devices".
 */
export function matchesFeature(perm, featureId) {
  if (typeof perm !== 'string' || !featureId) return false;
  const trimmed = perm.trim();
  const lowerTrimmed = trimmed.toLowerCase();
  const lowerFeature = featureId.trim().toLowerCase();

  // Exact match
  if (lowerTrimmed === lowerFeature) return true;

  // Exact match against standard action prefixes: e.g. "View Devices", "Create Devices"
  if (
    lowerTrimmed === `view ${lowerFeature}` ||
    lowerTrimmed === `create ${lowerFeature}` ||
    lowerTrimmed === `edit ${lowerFeature}` ||
    lowerTrimmed === `delete ${lowerFeature}`
  ) {
    return true;
  }

  // Segment-based match: `${parent}-${child}-${childAction}`
  const segments = trimmed.split('-');
  if (segments.length >= 3) {
    const permChild = segments[1].trim().toLowerCase();
    if (permChild === lowerFeature) {
      return true;
    }
  }

  return false;
}

/**
 * Checks if a permission string matches both the feature and the requested action.
 */
export function matchesFeatureAction(perm, featureId, action = 'read') {
  if (typeof perm !== 'string' || !featureId) return false;
  const trimmed = perm.trim();
  const lowerTrimmed = trimmed.toLowerCase();
  const lowerFeature = featureId.trim().toLowerCase();
  const lowerAction = (action || 'read').toLowerCase();

  const actionTerms = ACTION_TERMS_MAP[lowerAction] || [lowerAction];

  // Check alias format: e.g. "View Devices", "Edit Devices"
  for (const term of actionTerms) {
    if (lowerTrimmed === `${term} ${lowerFeature}`) {
      return true;
    }
  }

  // Check hyphenated format: `${parent}-${child}-${childAction}`
  const segments = trimmed.split('-');
  if (segments.length >= 3) {
    const permChild = segments[1].trim().toLowerCase();
    if (permChild === lowerFeature) {
      const actionPart = segments.slice(2).join('-').toLowerCase();
      return actionTerms.some((term) => actionPart.endsWith(term) || actionPart.includes(term));
    }
  }

  return false;
}

/**
 * Primary access check for a given user, feature, and action.
 *
 * @param {Object} user - Stored user principal (or null to read from localStorage)
 * @param {string} featureId - Feature name (e.g. 'Devices', 'Windows Agent Devices')
 * @param {string} action - 'read' | 'create' | 'update' | 'delete' | 'any'
 * @returns {boolean}
 */
export function hasFeatureAccess(user, featureId, action = 'read') {
  const principal = user || getStoredUser();
  if (!principal) return false;

  // Super Admin / Organization Owner bypass
  if (isOrganizationOwner(principal)) return true;

  // SCIM Connections special handling
  if (featureId === FEATURES.CONNECTIONS) {
    const scimPermissionMap = {
      read: SCIM_PERMISSIONS.READ,
      view: SCIM_PERMISSIONS.READ,
      create: SCIM_PERMISSIONS.CREATE,
      write: SCIM_PERMISSIONS.CREATE,
      update: SCIM_PERMISSIONS.UPDATE,
      edit: SCIM_PERMISSIONS.UPDATE,
      delete: SCIM_PERMISSIONS.DELETE,
      print: SCIM_PERMISSIONS.DELETE,
    };
    if (action === 'any') {
      return (
        hasScimPermission(principal, SCIM_PERMISSIONS.READ) ||
        hasScimPermission(principal, SCIM_PERMISSIONS.CREATE) ||
        hasScimPermission(principal, SCIM_PERMISSIONS.UPDATE) ||
        hasScimPermission(principal, SCIM_PERMISSIONS.DELETE)
      );
    }
    const requiredScimPerm = scimPermissionMap[(action || 'read').toLowerCase()] || SCIM_PERMISSIONS.READ;
    return hasScimPermission(principal, requiredScimPerm);
  }

  // Gateway Monitoring special handling
  if (featureId === FEATURES.GATEWAYS) {
    const gatewayPermissionMap = {
      read: GATEWAY_PERMISSIONS.READ,
      view: GATEWAY_PERMISSIONS.READ,
      create: GATEWAY_PERMISSIONS.CREATE,
      write: GATEWAY_PERMISSIONS.CREATE,
      update: GATEWAY_PERMISSIONS.UPDATE,
      edit: GATEWAY_PERMISSIONS.UPDATE,
      delete: GATEWAY_PERMISSIONS.DELETE,
      print: GATEWAY_PERMISSIONS.DELETE,
    };
    if (action === 'any') {
      return (
        hasGatewayPermission(principal, GATEWAY_PERMISSIONS.READ) ||
        hasGatewayPermission(principal, GATEWAY_PERMISSIONS.CREATE) ||
        hasGatewayPermission(principal, GATEWAY_PERMISSIONS.UPDATE) ||
        hasGatewayPermission(principal, GATEWAY_PERMISSIONS.DELETE)
      );
    }
    const requiredPerm = gatewayPermissionMap[(action || 'read').toLowerCase()] || GATEWAY_PERMISSIONS.READ;
    return hasGatewayPermission(principal, requiredPerm);
  }

  const permissions = Array.isArray(principal.permissions) ? principal.permissions : [];
  if (permissions.length === 0) return false;

  if (action === 'any') {
    return permissions.some((perm) => matchesFeature(perm, featureId));
  }

  return permissions.some((perm) => matchesFeatureAction(perm, featureId, action));
}

/**
 * Checks whether user has access to a specific route URL.
 */
export function canAccessPath(user, pathname) {
  const principal = user || getStoredUser();
  if (!principal) return false;
  if (isOrganizationOwner(principal)) return true;

  const normalized = (pathname || '').toLowerCase().replace(/\/$/, '') || '/';
  const matchedFeatureKey = Object.keys(ROUTE_FEATURE_MAP).find((route) => {
    const routeNorm = route.toLowerCase();
    return normalized === routeNorm || normalized.startsWith(`${routeNorm}/`);
  });

  if (!matchedFeatureKey) return true; // Unmapped paths (profile, etc.) remain open to authenticated users
  const feature = ROUTE_FEATURE_MAP[matchedFeatureKey];
  return hasFeatureAccess(principal, feature, 'read');
}

/**
 * Resolves the primary landing route for a user based on their assigned permissions.
 * Priority order: Dashboard -> Devices -> Windows Agent Devices -> AD Integration -> Applications -> Role Master -> User Management -> SCIM Connections
 */
export function getFirstAuthorizedRoute(user) {
  const principal = user || getStoredUser();
  if (!principal) return '/';

  if (isOrganizationOwner(principal)) return '/home';

  const orderedCandidates = [
    { feature: FEATURES.DASHBOARD, path: '/home' },
    { feature: FEATURES.DEVICES, path: '/devices' },
    { feature: FEATURES.WINDOWS_AGENT_DEVICES, path: '/windows-agent-devices' },
    // Retired: AD sync now runs through the gateway page
    // { feature: FEATURES.AD_INTEGRATION, path: '/admin/ad-integration' },
    { feature: FEATURES.APPLICATIONS, path: '/applications' },
    { feature: FEATURES.ROLE_MASTER, path: '/role' },
    { feature: FEATURES.USER_MANAGEMENT, path: '/user-management' },
    { feature: FEATURES.CONNECTIONS, path: '/admin/scim' },
  ];

  for (const candidate of orderedCandidates) {
    if (hasFeatureAccess(principal, candidate.feature, 'read')) {
      return candidate.path;
    }
  }

  return '/no-access';
}
